import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Container,
  Paper,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/FiberManualRecord";
import LoginImg from "../../../assets/loginImg.png";
import JusttawkLogo from "../../../../src/assets/loginImg2.png";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import axiosInstance from "../../../services/axiosInstance";

// Reusable styles
const centeredFlex = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const formatTime = (s) => {
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

const Otp = () => {
  const navigate = useNavigate();

  const userId = localStorage.getItem("userId");
  const isNew = localStorage.getItem("isNew");
  const emailNo = localStorage.getItem("emailNo"); // shown in UI
  const medium = localStorage.getItem("medium");
  const source = localStorage.getItem("source");
  const campaign = localStorage.getItem("campaign");

  // Resend OTP states
  const [resendTimer, setResendTimer] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const timerRef = useRef(null);

  const startResendCountdown = (seconds = 60) => {
    // reset
    setResendTimer(seconds);
    setIsResendDisabled(true);

    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          setIsResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    // start countdown on page load
    startResendCountdown(60);

    // cleanup on unmount
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const SendNewUserEmail = async (phoneNumber) => {
    try {
      if (!phoneNumber) {
        console.warn("Email is required to send email.");
        return;
      }

      const response = await axiosInstance.post(
        apiEndPointsConfig.sendNewUserDetailsEmail,
        { phoneNumber }
      );

      if (response.data.success) {
        console.log("New user email sent successfully.");
      } else {
        console.warn("Failed to send new user email:", response.data.message);
      }
    } catch (error) {
      console.error("Error sending new user email:", error.message);
    }
  };

  const verifyOtp = async (payload) => {
    try {
      const res = await axiosInstance.post(apiEndPointsConfig.otpVarification, payload);

      localStorage.setItem("token", res?.data?.user?.token);

      if (res?.data?.success === true) {
        if (isNew == 2) {
          navigate("/dashboard");
        } else {
          navigate("/form");
          SendNewUserEmail(emailNo);
        }
      } else {
        formik.setFieldError("otp", "Invalid OTP");
      }
    } catch (error) {
      console.error("Error:", error);
      formik.setFieldError("otp", "Invalid OTP");
    }
  };

  const resendOtp = async () => {
    try {
      setIsResendDisabled(true);

      const payload = {
        userId,
        email: emailNo,
        medium,
        source,
        campaign,
      };

      const endpoint =
        apiEndPointsConfig.resendOtp || apiEndPointsConfig.sendOtp;

      const res = await axiosInstance.post(apiEndPointsConfig.resendOtp, payload);

      if (res?.data?.success) {
        startResendCountdown(60);
      } else {
        setIsResendDisabled(false);
        console.warn(res?.data?.message || "Failed to resend OTP");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      setIsResendDisabled(false);
    }
  };

  const formik = useFormik({
    initialValues: { otp: "" },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required("OTP is required")
        .matches(/^\d{5}$/, "Must be a valid 5-digit OTP"),
    }),
    onSubmit: (values) => {
      verifyOtp({ userId, otp: values.otp });
    },
  });

  return (
    <Box>
      <Grid
        container
        sx={{
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column-reverse", md: "row" },
        }}
      >
        {/* Left Section */}
        <Grid item xs={12} md={6.5}>
          <Box
            sx={{
              display: "flex",
              minHeight: "100vh",
              position: "relative",
              mt: { xs: "-150px", md: 0 },
            }}
          >
            {/* Left blue sidebar */}
            <Box sx={{ display: "flex", width: "100%" }}>
              <Box
                sx={{
                  display: { xs: "none", md: "initial" },
                  width: "90%",
                  bgcolor: "#243e93",
                }}
              />
              <Box
                sx={{
                  display: { xs: "none", md: "initial" },
                  width: "10%",
                  bgcolor: "#3e55a0",
                }}
              />
            </Box>

            {/* Form Container */}
            <Container maxWidth="sm" sx={{ ...centeredFlex, p: 2 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 0, md: 5 },
                  borderRadius: 3,
                  width: "100%",
                  maxWidth: { xs: "90%", md: 500 },
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  border: "1px solid #dedede",
                  ml: { xs: 0, md: 5 },
                }}
              >
                <form onSubmit={formik.handleSubmit}>
                  <Box sx={{ p: { xs: 3, md: 0 } }}>
                    <Box sx={{ textAlign: "center", mb: 3 }}>
                      <Typography variant="h4" fontWeight="bold">
                        OTP Verification
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ mt: 0.5, fontWeight: 500 }}
                      >
                        OTP has been sent to your email {emailNo}. Please verify
                      </Typography>
                    </Box>

                    {/* OTP Input */}
                    <Box sx={{ my: 4 }}>
                      <Typography variant="body1" gutterBottom>
                        Please enter your OTP number*
                      </Typography>

                      <TextField
                        id="otp"
                        name="otp"
                        type="tel"
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        value={formik.values.otp}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.otp && Boolean(formik.errors.otp)}
                        helperText={formik.touched.otp && formik.errors.otp}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        sx={{ mb: 1.5 }}
                      />

                      {/* ✅ Resend row */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mt: 1,
                          mb: 2.5,
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {isResendDisabled
                            ? `Resend available in ${formatTime(resendTimer)}`
                            : "Didn’t receive OTP?"}
                        </Typography>

                        <Button
                          type="button"
                          onClick={resendOtp}
                          disabled={isResendDisabled}
                          variant="text"
                          sx={{
                            textTransform: "capitalize",
                            borderRadius: "8px",
                            borderColor: "#243e93",
                            color: "#243e93",
                            "&:hover": { borderColor: "#152a60" },
                            "&.Mui-disabled": {
                              opacity: 0.6,
                              borderColor: "#243e93",
                              color: "#243e93",
                            },
                          }}
                        >
                          Resend OTP
                        </Button>
                      </Box>

                      {/* OTP Requirements */}
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        OTP Requirements
                      </Typography>
                      <List disablePadding>
                        {[
                          "Must be a valid 5-digit OTP",
                          "Cannot contain any letters or special characters (e.g., +, -, or spaces)",
                        ].map((text, index) => (
                          <ListItem key={index} disableGutters sx={{ p: 0 }}>
                            <ListItemIcon sx={{ minWidth: 20 }}>
                              <CircleIcon
                                sx={{ fontSize: 10, color: "black" }}
                              />
                            </ListItemIcon>
                            <ListItemText primary={text} sx={{ m: 0, p: 0 }} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>

                    {/* Verify Button */}
                    <Button
                      variant="contained"
                      fullWidth
                      type="submit"
                      size="large"
                      sx={{
                        mt: 2,
                        bgcolor: "#243e93",
                        "&:hover": { bgcolor: "#152a60" },
                        py: 1.5,
                        textTransform: "capitalize",
                        borderRadius: "8px",
                      }}
                    >
                      Verify
                    </Button>
                  </Box>
                </form>
              </Paper>
            </Container>
          </Box>
        </Grid>

        {/* Right Section */}
        <Grid item xs={12} md={5.5}>
          <Box
            sx={{
              minHeight: "100%",
              p: 3,
              bgcolor: { xs: "#243E93E0", md: "white" },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
              }}
            >
              <img
                src={JusttawkLogo}
                alt="Justtawk Logo"
                style={{ maxWidth: 210 }}
              />
            </Box>

            <Grid container>
              <Grid item md={10} xs={12}>
                {/* Login Image */}
                <Box
                  sx={{
                    ...centeredFlex,
                    mt: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "100%", md: 400 },
                      display: { xs: "none", md: "initial" },
                    }}
                  >
                    <img
                      style={{ width: "100%" }}
                      src={LoginImg}
                      alt="Login Illustration"
                    />
                  </Box>
                </Box>

                {/* Welcome Text */}
                <Box sx={{ ...centeredFlex }}>
                  <Box
                    sx={{
                      maxWidth: 520,
                      textAlign: "center",
                      pb: { xs: "100px", md: "0" },
                    }}
                  >
                    <Typography
                      sx={{
                        color: { xs: "white", md: "black" },
                        fontSize: { xs: "1.5rem", md: "2.15rem" },
                        fontWeight: 900,
                      }}
                    >
                      Welcome to Justtawk
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        lineHeight: "25px",
                        mt: 1,
                        color: { xs: "white", md: "black" },
                        fontSize: { xs: "1rem", md: "1.2rem" },
                      }}
                    >
                      Affordable call center solution that helps your business
                      by seamlessly receiving and managing customer calls.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Otp;
